/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Typography } from '@equinor/eds-core-react';
import { CenteredLoader } from '../../../../components/CenteredLoader/CenteredLoader';
import {
  ArchelMeta,
  BBox,
  bboxToCanvas,
  CANVAS_H,
  CANVAS_W,
  clampBbox,
  GridData,
  renderCanvas,
} from '../modelAreaCoordinates.utils';
import { useModelAreasStore } from '../hooks/useModelAreasStore';
import * as Styled from './ArchelCanvas.styled';

// ─── Handle interaction types & helpers ──────────────────────────────────────

type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

type ActiveDrag =
  | {
      mode: 'new';
      startCx: number;
      startCy: number;
      currentCx: number;
      currentCy: number;
    }
  | {
      mode: 'move';
      startBbox: BBox;
      startCx: number;
      startCy: number;
      currentCx: number;
      currentCy: number;
    }
  | {
      mode: 'resize';
      handle: HandleId;
      startBbox: BBox;
      startCx: number;
      startCy: number;
      currentCx: number;
      currentCy: number;
    };

function getHandlePositions(
  bx0: number,
  bx1: number,
  by0: number,
  by1: number,
): Record<HandleId, [number, number]> {
  const mx = (bx0 + bx1) / 2;
  const my = (by0 + by1) / 2;
  return {
    nw: [bx0, by0],
    n: [mx, by0],
    ne: [bx1, by0],
    e: [bx1, my],
    se: [bx1, by1],
    s: [mx, by1],
    sw: [bx0, by1],
    w: [bx0, my],
  };
}

// Which model-space edges each handle moves when dragged.
// Canvas Y is inverted: top = high model Y (y1), bottom = low model Y (y0).
const HANDLE_AXES: Record<
  HandleId,
  Partial<Record<'x0' | 'x1' | 'y0' | 'y1', true>>
> = {
  nw: { x0: true, y1: true },
  n: { y1: true },
  ne: { x1: true, y1: true },
  e: { x1: true },
  se: { x1: true, y0: true },
  s: { y0: true },
  sw: { x0: true, y0: true },
  w: { x0: true },
};

const HANDLE_CURSORS: Record<HandleId, string> = {
  nw: 'nw-resize',
  n: 'ns-resize',
  ne: 'ne-resize',
  e: 'ew-resize',
  se: 'se-resize',
  s: 'ns-resize',
  sw: 'sw-resize',
  w: 'ew-resize',
};

const HIT_R = 10; // canvas-px hit radius for handles

function getHitZone(
  cx: number,
  cy: number,
  bbox: BBox | null,
  gridData: GridData,
): { zone: 'outside' } | { zone: 'inside' } | { zone: 'handle'; id: HandleId } {
  if (!bbox) return { zone: 'outside' };
  const { bx0, bx1, by0, by1 } = bboxToCanvas(bbox, gridData);
  const positions = getHandlePositions(bx0, bx1, by0, by1);
  for (const [id, [hx, hy]] of Object.entries(positions) as [
    HandleId,
    [number, number],
  ][]) {
    if (Math.abs(cx - hx) <= HIT_R && Math.abs(cy - hy) <= HIT_R) {
      return { zone: 'handle', id };
    }
  }
  if (cx >= bx0 - 4 && cx <= bx1 + 4 && cy >= by0 - 4 && cy <= by1 + 4) {
    return { zone: 'inside' };
  }
  return { zone: 'outside' };
}

function computeLiveBbox(drag: ActiveDrag, gridData: GridData): BBox | null {
  const { xMin, xMax, yMin, yMax } = gridData;
  const mDx = ((drag.currentCx - drag.startCx) / CANVAS_W) * (xMax - xMin);
  // Canvas Y is inverted: moving down (positive cy) decreases model Y
  const mDy = -((drag.currentCy - drag.startCy) / CANVAS_H) * (yMax - yMin);

  if (drag.mode === 'new') {
    if (
      Math.abs(drag.currentCx - drag.startCx) < 6 &&
      Math.abs(drag.currentCy - drag.startCy) < 6
    )
      return null;
    const mx0 = xMin + (drag.startCx / CANVAS_W) * (xMax - xMin);
    const my0 = yMin + (1 - drag.startCy / CANVAS_H) * (yMax - yMin);
    const mx1 = xMin + (drag.currentCx / CANVAS_W) * (xMax - xMin);
    const my1 = yMin + (1 - drag.currentCy / CANVAS_H) * (yMax - yMin);
    return clampBbox(
      {
        x0: Math.min(mx0, mx1),
        x1: Math.max(mx0, mx1),
        y0: Math.min(my0, my1),
        y1: Math.max(my0, my1),
      },
      gridData,
    );
  }

  const sb = drag.startBbox;
  if (drag.mode === 'move') {
    // Translate then clamp, keeping box size by shifting both edges together
    const w = sb.x1 - sb.x0;
    const h = sb.y1 - sb.y0;
    const cx0 = Math.max(xMin, Math.min(sb.x0 + mDx, xMax - w));
    const cy0 = Math.max(yMin, Math.min(sb.y0 + mDy, yMax - h));
    return { x0: cx0, x1: cx0 + w, y0: cy0, y1: cy0 + h };
  }

  // resize — move only the edges this handle controls
  const axes = HANDLE_AXES[drag.handle];
  const x0 = axes.x0 ? sb.x0 + mDx : sb.x0;
  const x1 = axes.x1 ? sb.x1 + mDx : sb.x1;
  const y0 = axes.y0 ? sb.y0 + mDy : sb.y0;
  const y1 = axes.y1 ? sb.y1 + mDy : sb.y1;
  // Normalise so x0<x1 and y0<y1 even if user drags past opposite edge
  return clampBbox(
    {
      x0: Math.min(x0, x1),
      x1: Math.max(x0, x1),
      y0: Math.min(y0, y1),
      y1: Math.max(y0, y1),
    },
    gridData,
  );
}

function computeTicks(min: number, max: number, step = 1000): number[] {
  if (max === min) return [min];
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.01; v += step) {
    ticks.push(Math.round(v));
  }
  return ticks;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ArchelCanvasProps {
  gridData: GridData | undefined;
  loading: boolean;
  error: string | null;
  archels: ArchelMeta[];
  showAxis?: boolean;
  maxWidth?: number;
  readOnly?: boolean;
}

export const ArchelCanvas = ({
  gridData,
  loading,
  error,
  archels,
  showAxis = true,
  maxWidth,
  readOnly = false,
}: ArchelCanvasProps) => {
  const storeBbox = useModelAreasStore((s) => s.bbox);
  const setBbox = useModelAreasStore((s) => s.setBbox);
  const interactive = useModelAreasStore((s) => s.editing);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<ActiveDrag | null>(null);
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [bbox, setBboxLocal] = useState<BBox | null>(null);
  const [cursor, setCursor] = useState<string>('crosshair');
  const [selectedLayer, setSelectedLayer] = useState<number | null>(null);

  // Sync from store → local canvas bbox (also clamp form-typed values to grid bounds)
  useEffect(() => {
    if (!storeBbox || !gridData) {
      setBboxLocal(storeBbox);
      return;
    }
    const clamped = clampBbox(storeBbox, gridData);
    setBboxLocal(clamped);
    // Write clamped value back to store so API calls always use in-bounds coords,
    // but only when the user is NOT actively editing via the form inputs —
    // otherwise clamping fights with typing (e.g. erasing "3901" to type "6000").
    if (!interactive) {
      const changed =
        clamped.x0 !== storeBbox.x0 ||
        clamped.x1 !== storeBbox.x1 ||
        clamped.y0 !== storeBbox.y0 ||
        clamped.y1 !== storeBbox.y1;
      if (changed) setBbox(clamped);
    }
  }, [storeBbox, gridData, setBbox, interactive]);

  // Push local canvas bbox → store
  const commitBbox = useCallback(
    (next: BBox | null) => {
      setBboxLocal(next);
      setBbox(next);
    },
    [setBbox],
  );

  // Default to last (shallowest) layer once grid data arrives
  useEffect(() => {
    if (gridData && selectedLayer === null) setSelectedLayer(gridData.nk - 1);
  }, [gridData, selectedLayer]);

  const layer = selectedLayer ?? (gridData ? gridData.nk - 1 : 0);

  const xTicks = useMemo(
    () => (gridData ? computeTicks(gridData.xMin, gridData.xMax) : []),
    [gridData],
  );
  const yTicks = useMemo(
    () => (gridData ? computeTicks(gridData.yMin, gridData.yMax) : []),
    [gridData],
  );

  // Live bbox = what's being dragged (or committed bbox when idle)
  const liveBbox = useMemo(
    () =>
      activeDrag && gridData ? computeLiveBbox(activeDrag, gridData) : bbox,
    [activeDrag, bbox, gridData],
  );

  useEffect(() => {
    if (canvasRef.current && gridData) {
      renderCanvas(
        canvasRef.current,
        null,
        liveBbox,
        gridData,
        layer,
        archels,
        interactive,
      );
    }
  }, [liveBbox, gridData, layer, archels, interactive]);

  const getScaledPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      cx: (e.clientX - rect.left) * (CANVAS_W / rect.width),
      cy: (e.clientY - rect.top) * (CANVAS_H / rect.height),
    };
  };

  const getCursorForZone = (hit: ReturnType<typeof getHitZone>): string => {
    if (hit.zone === 'handle') return HANDLE_CURSORS[hit.id];
    if (hit.zone === 'inside') return 'move';
    return 'crosshair';
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!gridData) return;
      const { cx, cy } = getScaledPos(e);
      const hit = getHitZone(cx, cy, bbox, gridData);
      let drag: ActiveDrag;
      if (hit.zone === 'handle' && bbox) {
        drag = {
          mode: 'resize',
          handle: hit.id,
          startBbox: bbox,
          startCx: cx,
          startCy: cy,
          currentCx: cx,
          currentCy: cy,
        };
      } else if (hit.zone === 'inside' && bbox) {
        drag = {
          mode: 'move',
          startBbox: bbox,
          startCx: cx,
          startCy: cy,
          currentCx: cx,
          currentCy: cy,
        };
        setCursor('grabbing');
      } else {
        drag = {
          mode: 'new',
          startCx: cx,
          startCy: cy,
          currentCx: cx,
          currentCy: cy,
        };
        commitBbox(null);
      }
      dragRef.current = drag;
      setActiveDrag(drag);
    },
    [bbox, gridData, commitBbox],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { cx, cy } = getScaledPos(e);
      if (dragRef.current) {
        const updated = {
          ...dragRef.current,
          currentCx: cx,
          currentCy: cy,
        } as ActiveDrag;
        dragRef.current = updated;
        setActiveDrag(updated);
        return;
      }
      // Hover — show cursor matching what a click/drag would do
      if (gridData) {
        setCursor(getCursorForZone(getHitZone(cx, cy, bbox, gridData)));
      }
    },
    [bbox, gridData],
  );

  const commitDrag = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const d = dragRef.current;
      dragRef.current = null;
      setActiveDrag(null);
      if (!d || !gridData) return;

      const live = computeLiveBbox(d, gridData);
      if (live) {
        commitBbox(live);
        const { cx, cy } = getScaledPos(e);
        setCursor(getCursorForZone(getHitZone(cx, cy, live, gridData)));
      } else {
        // new drag was too small — clear selection
        if (d.mode === 'new') {
          commitBbox(null);
        }
        setCursor('crosshair');
      }
    },
    [gridData, commitBbox],
  );

  return (
    <Styled.CanvasPanel $showAxis={showAxis} $maxWidth={maxWidth}>
      {(loading || error || !gridData) && (
        <Styled.LoadingBox width={CANVAS_W} height={CANVAS_H}>
          {error ? (
            <Typography variant="body_short">Error: {error}</Typography>
          ) : (
            <CenteredLoader size={48} />
          )}
        </Styled.LoadingBox>
      )}
      {gridData && (
        <Styled.CanvasWithAxis $showAxis={showAxis}>
          {showAxis && (
            <Styled.YAxisOverlay>
              {yTicks.map((v) => (
                <React.Fragment key={v}>
                  <Styled.TickMark
                    $axis="y"
                    $pct={
                      ((gridData.yMax - v) / (gridData.yMax - gridData.yMin)) *
                      100
                    }
                  />
                  <Styled.AxisTick
                    $axis="y"
                    $pct={
                      ((gridData.yMax - v) / (gridData.yMax - gridData.yMin)) *
                      100
                    }
                  >
                    {v.toLocaleString()}
                  </Styled.AxisTick>
                </React.Fragment>
              ))}
              <Styled.YAxisTitle>Y distance (m)</Styled.YAxisTitle>
            </Styled.YAxisOverlay>
          )}
          <Styled.StyledCanvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            onMouseDown={interactive ? handleMouseDown : undefined}
            onMouseMove={interactive ? handleMouseMove : undefined}
            onMouseUp={interactive ? commitDrag : undefined}
            onMouseLeave={interactive ? commitDrag : undefined}
            style={{ cursor: interactive ? cursor : 'default' }}
          />
          {showAxis && (
            <Styled.XAxisOverlay>
              {xTicks.map((v) => (
                <React.Fragment key={v}>
                  <Styled.TickMark
                    $axis="x"
                    $pct={
                      ((v - gridData.xMin) / (gridData.xMax - gridData.xMin)) *
                      100
                    }
                  />
                  <Styled.AxisTick
                    $axis="x"
                    $pct={
                      ((v - gridData.xMin) / (gridData.xMax - gridData.xMin)) *
                      100
                    }
                  >
                    {v.toLocaleString()}
                  </Styled.AxisTick>
                </React.Fragment>
              ))}
              <Styled.XAxisTitle>X distance (m)</Styled.XAxisTitle>
            </Styled.XAxisOverlay>
          )}
        </Styled.CanvasWithAxis>
      )}
      <Styled.SliderAligner $active={showAxis}>
        <Styled.CanvasHint>
          {(() => {
            const zLabel = `Z-layer ${layer} / ${(gridData?.nk ?? 1) - 1}`;
            if (readOnly) return zLabel;
            if (interactive)
              return `${zLabel} · drag to select · grab edge/corner to resize`;
            return `${zLabel} · press "Edit coordinates" to select`;
          })()}
        </Styled.CanvasHint>
        <Styled.CoordInfo>
          {liveBbox
            ? `X [${Math.round(liveBbox.x0)} – ${Math.round(
                liveBbox.x1,
              )}]  Y [${Math.round(liveBbox.y0)} – ${Math.round(
                liveBbox.y1,
              )}] m`
            : ' '}
        </Styled.CoordInfo>
      </Styled.SliderAligner>
      {gridData && (
        <Styled.SliderAligner $active={showAxis}>
          <Styled.LayerSliderRow>
            <Styled.LayerLabel>
              {`Z: ${layer} / ${gridData.nk - 1}` +
                (layer <= Math.floor(gridData.nk * 0.2)
                  ? ' ↓ deepest'
                  : layer >= Math.floor(gridData.nk * 0.8)
                  ? ' ↑ shallowest'
                  : '')}
            </Styled.LayerLabel>
            <Styled.RangeInput
              type="range"
              min={0}
              max={gridData.nk - 1}
              value={layer}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSelectedLayer(Number(e.target.value))
              }
            />
          </Styled.LayerSliderRow>
        </Styled.SliderAligner>
      )}
    </Styled.CanvasPanel>
  );
};
